import { forwardRef, useState, useEffect } from 'react';
import {
  Button,
  Form,
  Stack,
  OverlayTrigger,
  Tooltip,
  TooltipProps,
} from 'react-bootstrap';
import { AiOutlineSend } from 'react-icons/ai';

import './styles.css';

const MAX_MESSAGE_LENGTH = 5000;

interface MessageInputProps {
  onSubmitMessage: (value: string) => void;
  disabled?: boolean;
  inputDisabledForMember?: boolean;
  placeholder?: string;
  isSendingMessage?: boolean;
}

const MessageInput = forwardRef<HTMLTextAreaElement, MessageInputProps>(
  (
    {
      disabled,
      onSubmitMessage,
      inputDisabledForMember,
      isSendingMessage = false,
      placeholder = isSendingMessage
        ? 'Sending message...'
        : 'Enter your message here...',
    },
    ref
  ) => {
    const [messageText, setMessageText] = useState('');

    useEffect(() => {
      if (ref && typeof ref !== 'function' && ref.current) {
        const textarea = ref.current;
        textarea.style.height = 'auto';
        const { scrollHeight } = textarea;
        textarea.style.height = `${Math.min(scrollHeight, 250)}px`;
      }
    }, [messageText, ref]);

    const handleSubmitMessage = () => {
      if (messageText.trim().length === 0) return;
      onSubmitMessage(messageText.trim());
      setMessageText('');
      if (ref && typeof ref !== 'function' && ref?.current) {
        ref.current.focus();
      }
    };

    const renderTooltip = (props: TooltipProps) => (
      <Tooltip id="chat-input-tooltip" {...props}>
        <p className="m-0">
          Only tagged internal team members, please ask your admin, or someone
          tagged, to add you from edit job screen.
        </p>
      </Tooltip>
    );

    const formControl = (
      <Form.Control
        placeholder={placeholder}
        as="textarea"
        ref={ref}
        rows={1}
        maxLength={MAX_MESSAGE_LENGTH}
        onChange={(e) =>
          setMessageText(e.target.value.slice(0, MAX_MESSAGE_LENGTH))
        }
        disabled={disabled || inputDisabledForMember}
        value={messageText}
        className={isSendingMessage ? 'sending-message' : ''}
      />
    );

    return (
      <Stack
        direction="horizontal"
        className={`chat-input ${inputDisabledForMember ? 'disabled' : ''}`}
      >
        {inputDisabledForMember ? (
          <OverlayTrigger
            placement="top"
            delay={{ show: 250, hide: 400 }}
            overlay={renderTooltip}
          >
            {formControl}
          </OverlayTrigger>
        ) : (
          formControl
        )}
        <Button
          type="submit"
          variant="light"
          className={`submit-btn ${messageText.trim().length > 0 ? 'active' : ''}`}
          onClick={handleSubmitMessage}
          data-testid="send-button"
          disabled={
            disabled ||
            inputDisabledForMember ||
            messageText.trim().length === 0
          }
        >
          <AiOutlineSend size={20} />
        </Button>
      </Stack>
    );
  }
);

MessageInput.displayName = 'MessageInput';

export default MessageInput;
